import type { PianoChart } from '@shared/pianoChart';
import { MANIA_LANE_COUNT, type ManiaLane } from '@shared/maniaNote';
import { COMBO_BG_TIER_THRESHOLD, COMBO_BLOOM_PALETTE, LANE_GAP_PX } from './pianoRenderConstants';
import { comboTier, withAlpha } from './pianoRenderColour';
import type { PianoLayout } from '../pianoPlayfieldGeometry';
import type { PianoLaneFlash } from './pianoRenderTypes';
import type { RenderQuality } from '../../rendering/renderQuality';

/**
 * Stateless scene chrome — background fill + combo-tier bloom, the
 * per-lane idle/pressed backgrounds, the per-lane verdict flashes
 * spawned by the FX system, and the cyan/tier-coloured hit line at
 * the bottom of the lane stack. Wrapped in a class so the (expensive)
 * `createRadialGradient` for the bloom and `createLinearGradient`
 * for the pressed-lane wash can be cached across frames — on
 * mobile compositors a fresh gradient per frame is the single most
 * expensive item in the scene pass.
 *
 * Cache keys are the shape-defining inputs only — the bloom uses
 * `width|height|bloomColour` so a resize or combo-tier crossover
 * (which switches the bloom palette entry) invalidates and rebuilds,
 * and the pressed-lane wash uses `hitLineY|palette.player` so a
 * resize or chart swap rebuilds. The per-frame `bloomAlpha` ramp is
 * applied via `globalAlpha` at composite time so it doesn't force a
 * rebuild every frame.
 */
export class PianoSceneRenderer {
  private bgBloomGradient: CanvasGradient | null = null;
  private bgBloomKey = '';
  private pressGradient: CanvasGradient | null = null;
  private pressGradientKey = '';

  /**
   * Background fill plus a subtle radial bloom at the bottom that
   * shifts hue / intensity with combo tier. Below
   * COMBO_BG_TIER_THRESHOLD the bloom is dim and palette-padded so
   * early play stays visually stable; above it the bloom cycles
   * through a four-hue combo palette in lockstep with combo
   * milestones — the kiai-style "the music is getting hyped" cue.
   */
  drawBackground(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    palette: PianoChart['palette'],
    combo: number,
    reducedMotion: boolean,
    quality: RenderQuality,
  ): void {
    ctx.fillStyle = palette.bg;
    ctx.fillRect(0, 0, width, height);
    // The full-viewport radial bloom is the most expensive single
    // operation in the piano scene — a fresh `createRadialGradient`
    // over the entire canvas every frame. Reduced-motion users get a
    // flat fill anyway; low-tier devices skip it for fill-rate.
    if (reducedMotion || !quality.backgroundBloom) {
      return;
    }
    const tier = comboTier(combo);
    const bloomColour =
      combo >= COMBO_BG_TIER_THRESHOLD ? COMBO_BLOOM_PALETTE[tier] : palette.pad;
    const bloomAlpha = 0.08 + Math.min(0.18, combo / 200);
    // Cache key bakes in the *shape-defining* inputs (size + colour);
    // `bloomAlpha` is applied at composite time via globalAlpha so a
    // small combo ramp doesn't churn the gradient cache every frame.
    const key = `${width}|${height}|${bloomColour}`;
    if (!this.bgBloomGradient || this.bgBloomKey !== key) {
      const gradient = ctx.createRadialGradient(
        width / 2,
        height,
        0,
        width / 2,
        height,
        Math.max(width, height) * 0.9,
      );
      gradient.addColorStop(0, withAlpha(bloomColour, 1));
      gradient.addColorStop(1, withAlpha(bloomColour, 0));
      this.bgBloomGradient = gradient;
      this.bgBloomKey = key;
    }
    ctx.save();
    ctx.globalAlpha = bloomAlpha;
    ctx.fillStyle = this.bgBloomGradient;
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
  }

  drawLaneBackgrounds(
    ctx: CanvasRenderingContext2D,
    layout: PianoLayout,
    height: number,
    palette: PianoChart['palette'],
    pressedLanes: ReadonlySet<ManiaLane>,
  ): void {
    // Idle lane fills are flat — cheap, no gradient. Pressed lanes
    // overlay a downward gradient, which we cache because its shape
    // (size + palette) only changes on resize / chart swap.
    const pressKey = `${layout.hitLineY}|${palette.player}`;
    let pressGrad = this.pressGradient;
    if (!pressGrad || this.pressGradientKey !== pressKey) {
      pressGrad = ctx.createLinearGradient(0, 0, 0, layout.hitLineY);
      pressGrad.addColorStop(0, withAlpha(palette.player, 0.04));
      pressGrad.addColorStop(1, withAlpha(palette.player, 0.32));
      this.pressGradient = pressGrad;
      this.pressGradientKey = pressKey;
    }
    for (let l = 0; l < MANIA_LANE_COUNT; l += 1) {
      const x = layout.lane0X + l * (layout.laneWidth + LANE_GAP_PX);
      ctx.fillStyle = palette.bgAccent;
      ctx.fillRect(x, 0, layout.laneWidth, height);
      if (pressedLanes.has(l as ManiaLane)) {
        // Downward gradient peaking at the hit line — the lane reads as
        // "energised from the bottom up" rather than a flat tint, so a
        // pressed lane visually echoes where the action lands.
        ctx.fillStyle = pressGrad;
        ctx.fillRect(x, 0, layout.laneWidth, height);
      }
    }
  }

  drawLaneFlashes(
    ctx: CanvasRenderingContext2D,
    laneFlashes: ReadonlyArray<PianoLaneFlash>,
    layout: PianoLayout,
    height: number,
  ): void {
    if (laneFlashes.length === 0) {
      return;
    }
    for (const flash of laneFlashes) {
      const t = flash.life / flash.maxLife;
      const a = flash.alpha * t * t;
      if (a <= 0.01) {
        continue;
      }
      const x = layout.lane0X + flash.lane * (layout.laneWidth + LANE_GAP_PX);
      ctx.fillStyle = withAlpha(flash.colour, a);
      ctx.fillRect(x, 0, layout.laneWidth, height);
    }
  }

  drawHitLine(
    ctx: CanvasRenderingContext2D,
    layout: PianoLayout,
    palette: PianoChart['palette'],
    combo: number,
    reducedMotion: boolean,
    quality: RenderQuality,
  ): void {
    // Combo-tier hint: line gets brighter / glowier with combo so the
    // hit zone visually warms up as the player streaks.
    const intensity = Math.min(1, combo / 50);
    const colour =
      combo >= COMBO_BG_TIER_THRESHOLD ? COMBO_BLOOM_PALETTE[comboTier(combo)] : palette.padPerfect;
    ctx.save();
    if (!reducedMotion && quality.chromeShadows) {
      ctx.shadowColor = colour;
      ctx.shadowBlur = 8 + intensity * 16;
    }
    ctx.fillStyle = withAlpha(colour, 0.85);
    ctx.fillRect(layout.lane0X, layout.hitLineY - 2, layout.laneStackWidth, 4);
    // Soft underlay band that telegraphs the timing zone (purely
    // decorative; the hit-detection range is engine-owned).
    ctx.fillStyle = withAlpha(colour, 0.12 + intensity * 0.08);
    ctx.fillRect(layout.lane0X, layout.hitLineY - 18, layout.laneStackWidth, 36);
    ctx.restore();
  }
}
